// src/lib/sw-update/on-navigate.ts

import {useSwUpdateStore} from './store';
import {takeNewBuild} from './take-new-build';

/**
 * The slice of the router this needs. Narrower than `AnyRouter` so the
 * behaviour can be tested without standing up a real router and route tree.
 */
export interface ResolvedNavigationSource {
  subscribe: (
    eventType: 'onResolved',
    listener: (event: {pathChanged: boolean}) => void,
  ) => () => void;
}

/**
 * Take a pending update on the next navigation that changes route, instead of
 * waiting for the user to notice the banner.
 *
 * This is safe for a reason worth spelling out: a route change already tears
 * down the outgoing page's form state, so reloading at that exact moment
 * costs the user nothing they were not already losing. And where a route does
 * guard unsaved work — `useBlocker` in the photo-report template editor —
 * the blocker fires first and the navigation never resolves, so this never
 * runs. It follows the existing guard rather than second-guessing it.
 *
 * Returns an unsubscribe function.
 */
export function installUpdateOnNavigate(router: ResolvedNavigationSource): () => void {
  return router.subscribe('onResolved', (event) => {
    // Search-param changes (paging, sorting, tab switches) resolve as
    // navigations too. Reloading mid-table would read as a glitch and buys
    // nothing, so hold out for a real route change.
    if (!event.pathChanged) return;

    if (!useSwUpdateStore.getState().updateAvailable) return;

    // Double-firing is impossible: `takeNewBuild` refuses a second handoff.
    void takeNewBuild();
  });
}
